import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiBook,
  FiX,
  FiCheck,
  FiLoader,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown
} from "react-icons/fi";
import api from "../../../connector/api";

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 50, 100];

export default function SubjectTab() {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageSizeDropdownOpen, setIsPageSizeDropdownOpen] = useState(false);

  // Modal & Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);

  const [formData, setFormData] = useState({
    code: "",
    name: "",
    credits: 1,
    weekly_hours: 1,
    description: ""
  });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const pageSizeRef = useRef(null);

  // Fetch Subjects with DRF Server-Side Pagination
  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const res = await api.get("/academic/subjects/", {
        params: {
          page: currentPage,
          page_size: pageSize,
          search: searchQuery || undefined,
        },
      });

      if (res.data && Array.isArray(res.data.results)) {
        setSubjects(res.data.results);
        setTotalCount(res.data.count || 0);
      } else if (Array.isArray(res.data)) {
        setSubjects(res.data);
        setTotalCount(res.data.length);
      }
    } catch (err) {
      console.error("Failed to fetch subjects:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, [currentPage, pageSize, searchQuery]);

  // Modal Handlers
  const handleOpenModal = (item = null) => {
    setFormError("");
    if (item) {
      setEditingItem(item);
      setFormData({
        code: item.code || "",
        name: item.name || "",
        credits: item.credits ?? 1,
        weekly_hours: item.weekly_hours ?? 1,
        description: item.description || ""
      });
    } else {
      setEditingItem(null);
      setFormData({
        code: "",
        name: "",
        credits: 1,
        weekly_hours: 1,
        description: ""
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({
      code: "",
      name: "",
      credits: 1,
      weekly_hours: 1,
      description: ""
    });
    setFormError("");
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.name.trim()) {
      setFormError("Subject Name is required.");
      return;
    }

    setSubmitting(true);
    try {
      if (editingItem) {
        await api.put(`/academic/subjects/${editingItem.id}/`, formData);
      } else {
        await api.post("/academic/subjects/", formData);
      }
      handleCloseModal();
      fetchSubjects();
    } catch (err) {
      console.error("Error saving subject:", err);
      const errMsg =
        err.response?.data?.name?.[0] ||
        err.response?.data?.code?.[0] ||
        err.response?.data?.detail ||
        "Failed to save subject. Please check your inputs.";
      setFormError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Handler
  const handleDelete = async () => {
    if (!deletingItem) return;
    setSubmitting(true);
    try {
      await api.delete(`/academic/subjects/${deletingItem.id}/`);
      setIsDeleteModalOpen(false);
      setDeletingItem(null);
      fetchSubjects();
    } catch (err) {
      console.error("Error deleting subject:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Pagination Calculations
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const startIndex = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleSelectPageSize = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    setIsPageSizeDropdownOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Top Action Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Search Bar */}
        <div className="relative w-full sm:w-72">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <input
            type="text"
            placeholder="Search code or subject name..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 placeholder-zinc-400 focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
          />
        </div>

        {/* Add New Button */}
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-medium hover:bg-zinc-800 active:bg-zinc-950 transition-colors shrink-0"
        >
          <FiPlus className="w-3.5 h-3.5" />
          <span>Add Subject</span>
        </button>
      </div>

      {/* Data Table */}
      <div className="border border-zinc-200/80 rounded-lg overflow-hidden bg-white">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-zinc-400 space-y-2">
            <FiLoader className="w-6 h-6 animate-spin text-zinc-500" />
            <p className="text-xs font-medium">Loading subjects...</p>
          </div>
        ) : subjects.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[10px] font-bold text-zinc-500 uppercase tracking-wider">
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Subject Name</th>
                    <th className="px-4 py-3">Credits</th>
                    <th className="px-4 py-3">Weekly Hours</th>
                    <th className="px-4 py-3">Created Date</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-zinc-700">
                  {subjects.map((sub) => (
                    <tr key={sub.id} className="hover:bg-zinc-50/50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-zinc-900">
                        {sub.code || <span className="text-zinc-300 font-normal">N/A</span>}
                      </td>
                      <td className="px-4 py-3 font-medium text-zinc-800">
                        <div>
                          <span>{sub.name}</span>
                          {sub.description && (
                            <p className="text-[10px] text-zinc-400 truncate max-w-xs">
                              {sub.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-zinc-600">
                        {sub.credits}
                      </td>
                      <td className="px-4 py-3 font-medium text-zinc-600">
                        {sub.weekly_hours} hrs/wk
                      </td>
                      <td className="px-4 py-3 text-zinc-400">
                        {sub.created_at
                          ? new Date(sub.created_at).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <button
                          onClick={() => handleOpenModal(sub)}
                          className="p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                          title="Edit Subject"
                        >
                          <FiEdit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            setDeletingItem(sub);
                            setIsDeleteModalOpen(true);
                          }}
                          className="p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete Subject"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="px-4 py-3 border-t border-zinc-200/80 bg-zinc-50/50 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
              <div className="flex items-center gap-2">
                <span>Show</span>

                <div className="relative" ref={pageSizeRef}>
                  <button
                    type="button"
                    onClick={() => setIsPageSizeDropdownOpen(!isPageSizeDropdownOpen)}
                    className="flex items-center justify-between gap-2 px-2.5 py-1 bg-white border border-zinc-200/80 rounded-lg text-xs font-semibold text-zinc-800 hover:bg-zinc-50 focus:outline-none transition-colors"
                  >
                    <span>{pageSize}</span>
                    <FiChevronDown
                      className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
                        isPageSizeDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isPageSizeDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setIsPageSizeDropdownOpen(false)}
                      />
                      <div className="absolute bottom-full mb-1 left-0 w-20 bg-white border border-zinc-200/80 rounded-lg shadow-lg py-1 z-30 max-h-48 overflow-y-auto">
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <button
                            key={size}
                            onClick={() => handleSelectPageSize(size)}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left hover:bg-zinc-50 transition-colors ${
                              pageSize === size
                                ? "font-bold text-zinc-900 bg-zinc-50"
                                : "text-zinc-600"
                            }`}
                          >
                            <span>{size}</span>
                            {pageSize === size && (
                              <FiCheck className="w-3 h-3 text-zinc-700" />
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <span>entries</span>
                <span className="text-zinc-300 mx-1">|</span>
                <span>
                  Showing <strong className="text-zinc-800">{startIndex}</strong> to{" "}
                  <strong className="text-zinc-800">{endIndex}</strong> of{" "}
                  <strong className="text-zinc-800">{totalCount}</strong> entries
                </span>
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-md border border-zinc-200/80 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-white transition-colors"
                  title="Previous Page"
                >
                  <FiChevronLeft className="w-3.5 h-3.5" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                      currentPage === page
                        ? "bg-zinc-900 text-white"
                        : "bg-white border border-zinc-200/80 text-zinc-600 hover:bg-zinc-100"
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-md border border-zinc-200/80 bg-white text-zinc-600 hover:bg-zinc-100 disabled:opacity-40 disabled:hover:bg-white transition-colors"
                  title="Next Page"
                >
                  <FiChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center text-zinc-400 space-y-2">
            <FiBook className="w-8 h-8 text-zinc-300" />
            <p className="text-xs font-semibold text-zinc-700">No Subjects Found</p>
            <p className="text-[11px] text-zinc-400 max-w-xs">
              {searchQuery
                ? `No subject found matching "${searchQuery}".`
                : "Get started by adding your first academic subject above."}
            </p>
          </div>
        )}
      </div>

      {/* Add / Edit Subject Modal rendered using createPortal */}
      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs">
            <div className="bg-white rounded-lg font-dynamic-title border border-zinc-200/80 shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-150">
              <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
                <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider">
                  {editingItem ? "Edit Subject" : "Add Subject"}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-xs text-red-600">
                    <FiAlertCircle className="w-4 h-4 shrink-0" />
                    <span>{formError}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-zinc-700 mb-1">
                      Subject Code <span className="text-zinc-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      maxLength={20}
                      placeholder="e.g. MATH101"
                      value={formData.code}
                      onChange={(e) =>
                        setFormData({ ...formData, code: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-zinc-700 mb-1">
                      Subject Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={100}
                      placeholder="e.g. Mathematics"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-zinc-700 mb-1">
                      Credits
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={formData.credits}
                      onChange={(e) =>
                        setFormData({ ...formData, credits: parseFloat(e.target.value) || 0 })
                      }
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-zinc-700 mb-1">
                      Weekly Hours
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={formData.weekly_hours}
                      onChange={(e) =>
                        setFormData({ ...formData, weekly_hours: parseFloat(e.target.value) || 0 })
                      }
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-zinc-700 mb-1">
                    Description <span className="text-zinc-400 font-normal">(Optional)</span>
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Brief course description..."
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none focus:border-zinc-400 focus:bg-white transition-colors"
                  />
                </div>

                <div className="pt-2 flex items-center justify-end gap-2 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-3 py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-medium hover:bg-zinc-800 disabled:opacity-50 transition-colors"
                  >
                    {submitting ? (
                      <FiLoader className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FiCheck className="w-3.5 h-3.5" />
                    )}
                    <span>{editingItem ? "Update Subject" : "Save Subject"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs">
            <div className="bg-white rounded-lg font-dynamic-title border border-zinc-200/80 shadow-2xl w-full max-w-sm p-5 space-y-4 text-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto">
                <FiTrash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-zinc-900">Delete Subject</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Are you sure you want to delete <span className="font-bold text-zinc-800">{deletingItem?.name}</span>? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 pt-2">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="w-full py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  className="w-full py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                >
                  {submitting && <FiLoader className="w-3.5 h-3.5 animate-spin" />}
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}